import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';

import FieldContainer from './FieldContainer';
import InlineContainer from '@ad-scripts/presentation/components/common/containers/inline_container';
import InsetContainer from '@ad-scripts/presentation/components/common/containers/inset_container';
import StackContainer from '@ad-scripts/presentation/components/common/containers/stack_container';
import { H1, H2, H3 } from '@ad-scripts/presentation/components/common/headers';

type SectionProps = {
  'data-aid'?: string;
  className?: string;
  description?: string | ReactNode;
  header?: any;
  headerMargin?: 'none' | 'small' | 'medium' | 'large';
  id?: string;
};

type MainSectionProps = {
  'data-aid'?: string;
  className?: string;
  description?: string | ReactNode;
  header?: any;
  headerMargin?: 'none' | 'small' | 'medium' | 'large';
  id?: string;
};

const Section: FC<SectionProps> = ({
  'data-aid': dataAid,
  children,
  className,
  description,
  header,
  headerMargin = 'medium',
  id,
}) => {
  const showHeader = header || description;
  const renderHeader = showHeader ? (
    <SectionHeaderContainer inset="none" stack={headerMargin} data-aid={dataAid}>
      {header && <SectionHeader data-aid={`${id}-title`}>{header}</SectionHeader>}
      {description && <SectionDescription data-aid={`${id}-description`}>{description}</SectionDescription>}
    </SectionHeaderContainer>
  ) : null;

  return (
    <StyledSection className={className} data-aid={`${dataAid}-section`}>
      {renderHeader}
      {children}
    </StyledSection>
  );
};

// this component and Section will be renamed in this ticket to be more easily understandable
// https://app.shortcut.com/gladly/story/141895/rename-the-section-component-to-subsection-and-rename-the-mainsection-to-section
const MainSection: FC<MainSectionProps> = ({
  'data-aid': dataAid,
  children,
  className,
  description,
  header,
  headerMargin = 'medium',
  id,
}) => {
  const showHeader = header || description;
  const renderHeader = showHeader ? (
    <SectionHeaderContainer inset="none" stack={headerMargin} data-aid={dataAid}>
      {header && <MainSectionHeader data-aid={`${id}-title`}>{header}</MainSectionHeader>}
      {description && <SectionDescription data-aid={`${id}-description`}>{description}</SectionDescription>}
    </SectionHeaderContainer>
  ) : null;

  return (
    <StyledSection className={className} data-aid={`${dataAid}-section`}>
      {renderHeader}
      {children}
    </StyledSection>
  );
};

const PageHeader = ({ dataAid = 'page-header', headerText, descriptionText }) => (
  <PageHeaderContainer>
    <H1 data-aid={`${dataAid}-label`}>{headerText}</H1>
    {descriptionText ? (
      <PageHeaderDescription data-aid={`${dataAid}-description`}>{descriptionText}</PageHeaderDescription>
    ) : null}
  </PageHeaderContainer>
);

const PageHeaderContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const PageHeaderDescription = styled.div`
  align-self: center;
  font-size: ${p => p.theme.fontSize.base};
  color: ${p => p.theme.colors.gray700};
  line-height: 1.6;
  margin-bottom: ${p => p.theme.spacing.stackSmall};
  min-height: 18px;
`;

const FilledContainer = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 10px;
  margin-bottom: ${p => p.theme.spacing.insetMedium};
  padding: ${p => p.theme.spacing.stackMedium} ${p => p.theme.spacing.insetLarge};
`;

export const StyledSection = styled(StackContainer).attrs({ inset: 'none' })`
  margin-bottom: 24px;
`;

const SectionHeaderContainer = styled(StackContainer)`
  padding-bottom: 0;
`;

export const SectionHeader = styled(H3)`
  color: ${p => p.theme.colors.gray800};
`;

export const MainSectionHeader = styled(H2)`
  color: ${p => p.theme.colors.black};
`;

export const SectionDescription = styled(StackContainer).attrs({ stack: 'none', inset: 'none' })`
  color: ${p => p.theme.colors.gray700};
  line-height: 1.6;
`;

const Body = styled(InsetContainer).attrs({ inset: 'large' })`
  flex-grow: 1;
  max-width: 500px;
  width: 80%;
  padding-top: 0;
`;

const Row = styled(InlineContainer).attrs({ stack: 'medium', inset: 'none' })`
  display: flex;
  margin-bottom: 24px;

  > ${FieldContainer} {
    margin: 0 12px;
  }
  > ${FieldContainer}:first-child {
    margin-left: 0;
  }
  > ${FieldContainer}:last-child {
    margin-right: 0;
  }
`;

const FormSection = styled(Row)`
  flex-direction: column;
  margin-bottom: 0;
  line-height: 20px;

  & + & {
    margin-top: 16px;
  }
`;

const Description = styled.div`
  text-align: left;
  line-height: 20px;
  margin-bottom: 16px;
`;

export { Body, FilledContainer, PageHeader, Row, FormSection, Section, Description, MainSection };
